import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import { LocalStorageService } from '@services/local-storage.service';
import {
	TenantsService,
	type IGetTenantsParams,
} from '@services/tenants.service';

// --- Stores ---
import { TenantsStore } from '@stores/tenants.store';

// --- Utils ---
import { simpleDiff } from '@utils/simple-diff.util';

// --- Interfaces ---
import type { IFeatures, IModules, ITenant } from '@interfaces/tenants';

const REFRESH_INTERVAL = 120 * 1000; // 2 minutes

@Injectable({
	providedIn: 'root',
})
export class TenantsFacade {
	tenant!: ITenant;

	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);
	readonly refresh$ = this._refresh.asObservable();

	private readonly _filter = new BehaviorSubject<IGetTenantsParams>({});
	readonly filter$ = this._filter.asObservable().pipe(
		debounceTime(300),
		distinctUntilChanged((prev, curr) => !simpleDiff(prev, curr))
	);

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.refresh$,
		this.autoRefresh$,
	]).pipe(
		switchMap(([filter]) => this.getTenants({ ...filter, pageLimit: 10 }))
	);

	readonly tenantsState$ = combineLatest([
		this.tenantsStore.tenantsState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => state),
		shareReplay({ refCount: true })
	);

	constructor(
		private localStorageService: LocalStorageService,
		private tenantsService: TenantsService,
		private tenantsStore: TenantsStore
	) {
		this.tenant = this.localStorageService.get('tenant') as ITenant;
	}

	getTenants(queryParams?: IGetTenantsParams) {
		return this.tenantsService
			.getTenants(queryParams)
			.pipe(tap(tenants => this.tenantsStore.updateTenants(tenants)));
	}

	getTenantById(id: ITenant['id']) {
		return this.tenantsService.getTenantById(id);
	}

	newTenant(tenant: ITenant) {
		return this.tenantsService
			.newTenant(tenant)
			.pipe(tap(tenant => this.tenantsStore.updateTenant(tenant)));
	}

	updateTenant(id: ITenant['id'], tenant: ITenant) {
		return this.tenantsService
			.updateTenant(id, tenant)
			.pipe(tap(() => this.tenantsStore.updateTenant(tenant)));
	}

	updateFeatures(id: ITenant['id'], features: IFeatures) {
		return this.tenantsService.updateFeatures(id, features);
	}

	deleteTenant(tenant: ITenant) {
		return this.tenantsService
			.deleteTenant(tenant.id)
			.pipe(tap(() => this.tenantsStore.deleteTenant(tenant)));
	}

	activateTenant(tenant: ITenant) {
		return this.tenantsService
			.activate(tenant.id)
			.pipe(tap(() => this.tenantsStore.deleteTenant(tenant)));
	}

	filterTenants(filter: IGetTenantsParams) {
		this._filter.next({ ...this._filter.value, ...filter });
	}

	syncLocalTenant(tenant: ITenant) {
		this.localStorageService.set('tenant', tenant);
		this.tenant = tenant;
	}

	// Planos
	getPricingTiers() {
		return this.tenantsService.getPricingTiers();
	}

	hasFeatures(feature: keyof IFeatures) {
		return Boolean(this.tenant?.features?.[feature]);
	}

	hasModule(m: keyof IModules) {
		return Boolean(this.tenant?.modules?.[m]);
	}

	hasFlag(flag: keyof ITenant) {
		return Boolean(this.tenant?.[flag]);
	}
}
